import type { CheckInCampus } from '../types';

export const CENTER_TIME_ZONE = 'Asia/Ho_Chi_Minh';

export interface DevicePosition {
    latitude: number;
    longitude: number;
    accuracyM: number;
}

export type LocationErrorReason = 'denied' | 'unavailable' | 'timeout' | 'unsupported' | 'other';

export class LocationError extends Error {
    reason: LocationErrorReason;

    constructor(message: string, reason: LocationErrorReason = 'other') {
        super(message);
        this.name = 'LocationError';
        this.reason = reason;
    }
}

function errorReason(error: GeolocationPositionError): LocationErrorReason {
    if (error.code === error.PERMISSION_DENIED) return 'denied';
    if (error.code === error.POSITION_UNAVAILABLE) return 'unavailable';
    if (error.code === error.TIMEOUT) return 'timeout';
    return 'other';
}

function describeGeolocationError(error: GeolocationPositionError) {
    if (error.code === error.PERMISSION_DENIED) {
        return 'Trang này đang bị chặn quyền vị trí.';
    }
    if (error.code === error.POSITION_UNAVAILABLE) {
        return 'Thiết bị không xác định được vị trí. Hãy bật GPS/Wi-Fi rồi thử lại.';
    }
    if (error.code === error.TIMEOUT) {
        return 'Hết thời gian chờ định vị. Hãy thử lại ở nơi thoáng hơn.';
    }
    return 'Không lấy được vị trí.';
}

export function getCurrentPosition(timeoutMs = 15000): Promise<DevicePosition> {
    return new Promise((resolve, reject) => {
        if (!window.isSecureContext || !('geolocation' in navigator)) {
            reject(new LocationError('Trình duyệt này không hỗ trợ định vị.', 'unsupported'));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            position => resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracyM: Math.round(position.coords.accuracy),
            }),
            error => reject(new LocationError(describeGeolocationError(error), errorReason(error))),
            { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30000 },
        );
    });
}

// Theo dõi quyền vị trí (nếu trình duyệt hỗ trợ). Gọi onChange khi người dùng bật/tắt quyền trong cài đặt.
// Trả về hàm huỷ theo dõi.
export function watchLocationPermission(onChange: (state: PermissionState) => void): () => void {
    let status: PermissionStatus | null = null;
    let cancelled = false;
    const handle = () => { if (status) onChange(status.state); };
    navigator.permissions?.query({ name: 'geolocation' as PermissionName })
        .then(result => {
            if (cancelled) return;
            status = result;
            result.addEventListener('change', handle);
        })
        .catch(() => undefined);
    return () => {
        cancelled = true;
        status?.removeEventListener('change', handle);
    };
}

export async function readLocationPermission(): Promise<PermissionState | null> {
    try {
        const result = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
        return result?.state ?? null;
    } catch {
        return null;
    }
}

export function distanceMeters(
    from: { latitude: number; longitude: number },
    to: { latitude: number; longitude: number },
) {
    const earthRadiusM = 6371000;
    const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
    const dLat = toRadians(to.latitude - from.latitude);
    const dLng = toRadians(to.longitude - from.longitude);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRadians(from.latitude)) * Math.cos(toRadians(to.latitude)) * Math.sin(dLng / 2) ** 2;
    return 2 * earthRadiusM * Math.asin(Math.sqrt(a));
}

// Mirrors the server rule, for display only: the server decides the recorded status.
export function isWithinCampus(campus: CheckInCampus, position: DevicePosition, distanceM: number) {
    return distanceM <= campus.checkInRadiusM + Math.min(Math.max(position.accuracyM, 0), 100);
}

export interface CampusDistance {
    campus: CheckInCampus;
    distanceM: number;
    inRange: boolean;
}

export function rankCampusesByDistance(campuses: CheckInCampus[], position: DevicePosition): CampusDistance[] {
    return campuses
        .filter(campus => campus.latitude !== null && campus.longitude !== null)
        .map(campus => {
            const distanceM = Math.round(distanceMeters(position, {
                latitude: campus.latitude!,
                longitude: campus.longitude!,
            }));
            return { campus, distanceM, inRange: isWithinCampus(campus, position, distanceM) };
        })
        .sort((a, b) => a.distanceM - b.distanceM);
}

export function formatDistance(distanceM: number) {
    return distanceM < 1000 ? `${distanceM}m` : `${(distanceM / 1000).toFixed(1).replace('.', ',')}km`;
}

// "HH:mm" in Vietnam time (GMT+7), whatever the device time zone is.
export function formatCenterTime(value: string | Date) {
    return new Date(value).toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: CENTER_TIME_ZONE,
    });
}

export function centerDateKey(value: Date = new Date()) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: CENTER_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(value);
}

// Accepts "10.7769, 106.7009" as copied from Google Maps.
export function parseCoordinates(value: string): { latitude: number; longitude: number } | null {
    const match = value.trim().match(/^(-?\d{1,2}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)$/);
    if (!match) return null;
    const latitude = Number(match[1]);
    const longitude = Number(match[2]);
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
    return { latitude, longitude };
}
